import type { ClientStats } from '@hemmafixa/shared';
import {
  Avatar,
  Button,
  Card,
  ChartCard,
  ContextPanel,
  DonutChart,
  EmptyState,
  HighlightCard,
  LineChart,
  PageHeader,
  ProfileCard,
  StatStrip,
  StatusBadge,
} from '@hemmafixa/ui';
import {
  ArrowRight,
  Briefcase,
  HandCoins,
  Mail,
  MessageSquare,
  Phone,
  Plus,
  Sparkles,
  Trophy,
} from 'lucide-react';
import { Link } from 'react-router';
import { useCurrentUser } from '@/features/auth/api';
import { firstName } from '@/features/auth/access';
import { fill, useI18n } from '@/i18n/context';
import { useCategoryLabel } from '../shared/catalogue';
import { linkTo } from '../shared/link-to';
import { OverviewTodos, RecentActivity } from '../shared/overview-parts';
import { QueryView } from '../shared/query-view';
import { useStatuses } from '../shared/statuses';
import { countOf, useMonthPoints } from '../shared/stats-parts';
import { useClientCharts, useClientOverview, useMyJobs } from './api';

/**
 * The client's start page (P5-07, UI-06): what needs them, a few numbers, charts of the last six months and their
 * active jobs. The context panel holds the profile card, a call to post a job and recent activity.
 */
export function ClientOverviewPage() {
  const user = useCurrentUser();
  const { t } = useI18n();
  const overview = useClientOverview();
  const charts = useClientCharts();
  const numbers = overview.data;
  const o = t.client.overview;

  return (
    <div className="grid gap-4">
      <PageHeader
        title={fill(o.title, { name: firstName(user.name) })}
        description={o.description}
        actions={
          <Button asChild>
            <a href="https://hemmafixa.se">
              <Plus /> {t.client.postJob}
            </a>
          </Button>
        }
      />
      <StatStrip
        loading={overview.isPending}
        items={[
          {
            icon: Briefcase,
            tone: 'info' as const,
            label: o.activeJobs,
            value: numbers?.activeJobs ?? 0,
            link: linkTo('/client/jobs'),
          },
          {
            icon: HandCoins,
            tone: 'brand' as const,
            label: o.newProposals,
            value: numbers?.proposalsToAnswer ?? 0,
            link: linkTo('/client/jobs'),
          },
          {
            icon: MessageSquare,
            tone: 'success' as const,
            label: o.unreadMessages,
            value: numbers?.unreadMessages ?? 0,
            link: linkTo('/client/messages'),
          },
          {
            icon: Trophy,
            tone: 'brand' as const,
            label: o.chosenJobs,
            value: numbers?.chosenJobs ?? 0,
            link: linkTo('/client/jobs'),
          },
        ]}
      />
      <JobCharts stats={charts.data} loading={charts.isPending} />
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <OverviewTodos todos={numbers?.todo ?? []} area="client" loading={overview.isPending} />
        <ActiveJobs />
      </div>
      <ContextPanel>
        <ProfileCard
          avatar={<Avatar seed={user.id} name={user.name} size="xl" />}
          name={user.name}
          subtitle={t.areas.client}
          details={[
            { icon: Mail, label: t.settings.email, value: user.email },
            ...(user.phone ? [{ icon: Phone, label: t.settings.phone, value: user.phone }] : []),
          ]}
          stats={[
            { label: t.charts.active, value: numbers?.activeJobs ?? 0 },
            { label: t.charts.proposals, value: numbers?.proposalsToAnswer ?? 0 },
            { label: t.charts.unread, value: numbers?.unreadMessages ?? 0 },
          ]}
        />
        <HighlightCard
          icon={Sparkles}
          title={t.dashboard.postJobTitle}
          action={
            <Button asChild size="sm">
              <a href="https://hemmafixa.se">
                <Plus /> {t.client.postJob}
              </a>
            </Button>
          }
        >
          {t.dashboard.postJobText}
        </HighlightCard>
        <RecentActivity area="client" />
      </ContextPanel>
    </div>
  );
}

function JobCharts({ stats, loading }: { stats?: ClientStats; loading: boolean }) {
  const { t } = useI18n();
  const months = useMonthPoints();
  const c = t.charts;
  const statuses = t.status.job;
  const rows = stats?.jobStatuses ?? [];
  const jobs = months(stats?.jobsPerMonth ?? []);
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  return (
    <div className="grid items-stretch gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <ChartCard title={c.jobsAndProposals} description={c.lastSixMonths} loading={loading}>
        <LineChart
          title={c.jobsAndProposals}
          labels={jobs}
          series={[
            {
              name: c.proposalsReceived,
              color: 1,
              values: (stats?.proposalsPerMonth ?? []).map((p) => p.value),
            },
            { name: c.jobs, color: 2, values: jobs.map((point) => point.value) },
          ]}
        />
      </ChartCard>
      <ChartCard title={c.yourJobs} loading={loading}>
        <DonutChart
          title={c.yourJobs}
          centerValue={total}
          centerLabel={c.jobsCenter}
          segments={[
            { label: c.inReview, value: countOf(rows, ['draft', 'in_review', 'needs_info']), color: 1 },
            { label: statuses.open, value: countOf(rows, ['open']), color: 2 },
            { label: statuses.provider_chosen, value: countOf(rows, ['provider_chosen']), color: 3 },
            { label: c.other, value: countOf(rows, ['rejected', 'cancelled', 'expired']), color: 'muted' },
          ]}
        />
      </ChartCard>
    </div>
  );
}

function ActiveJobs() {
  const { t, relative } = useI18n();
  const statuses = useStatuses();
  const categoryLabel = useCategoryLabel();
  const query = useMyJobs({ filter: 'active', pageSize: 5 });

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <h2 className="text-sm font-semibold">{t.client.overview.activeJobs}</h2>
        <Button asChild variant="more" size="sm">
          <Link to="/client/jobs">
            {t.common.viewAll} <ArrowRight />
          </Link>
        </Button>
      </div>
      <QueryView query={query} loading="list">
        {({ items }) =>
          items.length === 0 ? (
            <EmptyState
              icon={Briefcase}
              title={t.client.jobs.empty}
              description={t.client.jobs.emptyText}
              className="py-8"
            />
          ) : (
            <ul className="divide-y divide-border">
              {items.map((job) => (
                <li key={job.id}>
                  <Link
                    to={`/client/jobs/${job.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-muted/60"
                  >
                    <span className="grid min-w-0 gap-0.5">
                      <span className="truncate text-sm font-medium">{job.title}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {categoryLabel(job.categorySlug)}
                        {job.submittedAt ? ` · ${relative(job.submittedAt)}` : ''} ·{' '}
                        {fill(t.client.proposalCount, { count: job.proposalCount })}
                      </span>
                    </span>
                    <StatusBadge status={job.status} statuses={statuses.job} />
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </Card>
  );
}
